import React, { useEffect, useState } from "react"
import {
  Box,
  FormControl,
  FormLabel,
  Slider as ChakraSlider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from "@chakra-ui/react"
import { Control, useController } from "react-hook-form"

type Props = {
  name: string;
  control: Control<any>;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
  label?: string;
  disabled?: boolean;
  error?: boolean;
}

const Slider = ({
  name,
  control,
  min = 0,
  max = 100,
  step = 1,
  defaultValue,
  label,
  disabled,
  error,
}: Props) => {
  const { field } = useController({ name, control, defaultValue })
  const [value, setValue] = useState<number>(field.value)

  // Sync local state when the form value changes externally (e.g. the side
  // panel resets to a different block). Without this the thumb keeps showing
  // the previous block's value and a drag would write that stale value.
  useEffect(() => {
    setValue(field.value)
  }, [field.value])

  useEffect(() => {
    if (max < value) {
      setValue(max)
    }
  }, [max])

  return (
    <FormControl mb={4} isInvalid={error} display="flex" alignItems="center">
      {label && <FormLabel w={1 / 4} mb={0}>{label}</FormLabel>}
      <ChakraSlider
        name={name}
        aria-label={label}
        flex="1"
        min={min}
        max={max}
        step={step}
        value={value}
        isDisabled={disabled}
        onChange={(v) => {
          setValue(v)
        }}
        onChangeEnd={(v) => {
          if (v === field.value) {
            return
          }

          field.onChange(v)
        }}
        focusThumbOnChange={false}
      >
        <SliderTrack>
          <SliderFilledTrack />
        </SliderTrack>
        <SliderThumb borderWidth="1px" borderColor="gray.400" />
      </ChakraSlider>
      <Box ml={3} minW="2.5ch" flexShrink={0} textAlign="right" fontSize="sm" color="gray.600">
        {value}
      </Box>
    </FormControl>
  )
}

export default Slider
